import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { isAdmin } from "@/lib/permissions";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import { Sparkles, Loader2, Coins, Crown, MessageCircle, X, Send, ChevronRight } from "lucide-react";

/**
 * Re-usable upsell card / modal that nudges the user to upgrade or buy credits.
 *
 * Usage examples:
 *   <UpgradeNudge featureKey="map_builder" reason="locked" />
 *   <UpgradeNudge featureKey="learn_coach_plan" reason="low_credits" />
 *   <UpgradeNudge featureKey="claim" variant="banner" />
 *
 * Variants: 'card' (default, embedded card), 'banner' (thin top banner), 'modal' (full overlay)
 */
export default function UpgradeNudge({
  featureKey,
  reason = "locked",     // 'locked' | 'low_credits' | 'rate_limited'
  variant = "card",      // 'card' | 'banner' | 'modal'
  onClose,
  title,
  message,
}) {
  const { user } = useAuth();
  const { lang } = useT();
  const navigate = useNavigate();
  const [chatOpen, setChatOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]); // [{role, text, action, action_label}]

  // Admins see the full product — never show them upsells.
  if (isAdmin(user)) return null;

  const askAudrey = async (q) => {
    const text = (q || question).trim();
    if (!text) return;
    setBusy(true);
    setHistory((h) => [...h, { role: "user", text }]);
    setQuestion("");
    try {
      const { data } = await api.post("/pro/sales-chat", {
        feature_key: featureKey || "subscription",
        question: text,
        lang,
      });
      setHistory((h) => [
        ...h,
        {
          role: "ai",
          text: data.answer,
          arguments: data.arguments || [],
          action: data.best_action,
          action_label: data.action_label,
          honest_note: data.honest_note,
          feature: data.feature,
        },
      ]);
    } catch (e) {
      setHistory((h) => [
        ...h,
        { role: "ai", text: e?.response?.data?.detail || (lang === "fr" ? "geoVINUM AI est indisponible." : "geoVINUM AI unavailable.") },
      ]);
    } finally { setBusy(false); }
  };

  const handleAction = (action) => {
    if (action === "buy_credits") { navigate("/pricing#credit-packs"); return; }
    if (action === "upgrade_pro" || action === "upgrade_amateur") { navigate("/pricing"); return; }
    if (action === "request_demo") { navigate("/pro#contact"); return; }
    navigate("/pricing");
  };

  const isPaid = user?.subscription_active;
  const defaultTitle =
    reason === "low_credits"
      ? (lang === "fr" ? "Crédits insuffisants" : "Not enough credits")
      : reason === "rate_limited"
        ? (lang === "fr" ? "Quota atteint" : "Quota reached")
        : (lang === "fr" ? "Fonctionnalité Pro" : "Pro feature");

  const defaultMessage = (() => {
    if (reason === "low_credits") {
      return lang === "fr"
        ? "Achetez un pack ou passez sur Amateur (29 €/mois · 100 crédits inclus) pour continuer."
        : "Buy a pack or switch to Amateur (€29/mo · 100 credits) to continue.";
    }
    if (reason === "rate_limited") {
      return lang === "fr"
        ? "Votre quota gratuit est atteint. L'abonnement Amateur supprime cette limite."
        : "You've hit the free quota. Amateur lifts this limit.";
    }
    return isPaid
      ? (lang === "fr" ? "Cette feature nécessite le plan Pro (49 €/mois)." : "This feature requires the Pro plan (€49/mo).")
      : (lang === "fr" ? "Débloquez cette feature avec un plan payant." : "Unlock this feature with a paid plan.");
  })();

  const showTitle = title || defaultTitle;
  const showMessage = message || defaultMessage;

  // BANNER variant
  if (variant === "banner") {
    return (
      <div className="rounded-2xl bg-gradient-to-r from-[#FCE4ED] to-white border-l-4 border-[#E5276A] p-4 flex items-center gap-3 flex-wrap" data-testid={`upgrade-nudge-banner-${featureKey || "default"}`}>
        <Sparkles className="w-5 h-5 text-[#E5276A] shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-[#1F3A5F]">{showTitle}</p>
          <p className="text-xs text-[#4A5568]">{showMessage}</p>
        </div>
        <button onClick={() => setChatOpen(true)} data-testid="upgrade-nudge-ask"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#1F3A5F]/15 text-xs font-bold text-[#1F3A5F] hover:border-[#E5276A]">
          <MessageCircle className="w-3.5 h-3.5" /> {lang === "fr" ? "Poser une question" : "Ask"}
        </button>
        <button onClick={() => navigate("/pricing")} data-testid="upgrade-nudge-cta"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#E5276A] text-white text-xs font-bold hover:bg-[#C01661]">
          {reason === "low_credits" ? (lang === "fr" ? "Acheter" : "Buy") : (lang === "fr" ? "Voir les plans" : "See plans")}
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
        {chatOpen && <ChatPanel inline featureKey={featureKey} onClose={() => setChatOpen(false)} history={history} setHistory={setHistory} ask={askAudrey} q={question} setQ={setQuestion} busy={busy} lang={lang} onAction={handleAction} />}
      </div>
    );
  }

  // MODAL variant
  if (variant === "modal") {
    return (
      <div className="fixed inset-0 z-50 bg-[#1F3A5F]/60 flex items-end md:items-center justify-center md:p-5 overlay-safe" data-testid={`upgrade-nudge-modal-${featureKey || "default"}`}>
        <div className="bg-white max-w-lg w-full shadow-2xl relative flex flex-col
                        rounded-t-3xl md:rounded-3xl
                        max-h-[calc(100dvh_-_var(--safe-top)_-_0.5rem)] md:max-h-[85dvh] pb-safe md:pb-0">
          {onClose && (
            <button onClick={onClose} className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-[#FBF7F3] hover:bg-[#FCE4ED] text-[#1F3A5F] hover:text-[#E5276A] flex items-center justify-center shadow-sm" data-testid="upgrade-nudge-close" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
          )}
          <div className="overflow-y-auto p-6 md:p-7">
          <div className="w-12 h-12 rounded-full bg-[#FCE4ED] flex items-center justify-center">
            {reason === "low_credits" ? <Coins className="w-6 h-6 text-[#E5276A]" /> : <Crown className="w-6 h-6 text-[#E5276A]" />}
          </div>
          <h2 className="font-display text-2xl text-[#1F3A5F] mt-4">{showTitle}</h2>
          <p className="text-sm text-[#4A5568] mt-2">{showMessage}</p>
          <div className="mt-5 flex gap-2 flex-wrap">
            <button onClick={() => navigate("/pricing")} data-testid="upgrade-nudge-cta"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661]">
              {reason === "low_credits" ? (lang === "fr" ? "Acheter des crédits" : "Buy credits") : (lang === "fr" ? "Voir les plans" : "See plans")}
              <ChevronRight className="w-4 h-4" />
            </button>
            <button onClick={() => setChatOpen(true)} data-testid="upgrade-nudge-ask"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A]">
              <MessageCircle className="w-4 h-4" /> {lang === "fr" ? "Poser une question à geoVINUM AI" : "Ask geoVINUM AI"}
            </button>
          </div>
          {chatOpen && <ChatPanel inline featureKey={featureKey} onClose={() => setChatOpen(false)} history={history} setHistory={setHistory} ask={askAudrey} q={question} setQ={setQuestion} busy={busy} lang={lang} onAction={handleAction} />}
          </div>
        </div>
      </div>
    );
  }

  // CARD variant (default)
  return (
    <div className="app-card p-5 border-l-4 border-[#E5276A]" data-testid={`upgrade-nudge-card-${featureKey || "default"}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-[#FCE4ED] flex items-center justify-center shrink-0">
          {reason === "low_credits" ? <Coins className="w-5 h-5 text-[#E5276A]" /> : <Crown className="w-5 h-5 text-[#E5276A]" />}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-bold text-[#1F3A5F] text-sm">{showTitle}</h3>
          <p className="text-xs text-[#4A5568] mt-1">{showMessage}</p>
          <div className="mt-3 flex gap-2 flex-wrap">
            <button onClick={() => navigate("/pricing")} data-testid="upgrade-nudge-cta"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#E5276A] text-white text-xs font-bold hover:bg-[#C01661]">
              {reason === "low_credits" ? (lang === "fr" ? "Acheter" : "Buy") : (lang === "fr" ? "Voir les plans" : "See plans")}
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => setChatOpen((v) => !v)} data-testid="upgrade-nudge-ask"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#1F3A5F]/15 text-xs font-bold text-[#1F3A5F] hover:border-[#E5276A]">
              <MessageCircle className="w-3.5 h-3.5" /> {lang === "fr" ? "J'ai une question" : "I have a question"}
            </button>
          </div>
        </div>
      </div>
      {chatOpen && <ChatPanel inline featureKey={featureKey} onClose={() => setChatOpen(false)} history={history} setHistory={setHistory} ask={askAudrey} q={question} setQ={setQuestion} busy={busy} lang={lang} onAction={handleAction} />}
    </div>
  );
}

// ─── Reusable chat panel ──────────────────────────────
function ChatPanel({ inline, featureKey, onClose, history, ask, q, setQ, busy, lang, onAction }) {
  const SUGGESTIONS = lang === "fr"
    ? ["Combien ça coûte vraiment ?", "Concrètement, ça m'apporte quoi ?", "Et si ça ne me convient pas ?", "Comparez avec la version gratuite", "Combien de temps pour démarrer ?"]
    : ["What does it really cost?", "What's the concrete value?", "What if it doesn't fit?", "Compare with the free version?", "How long to get started?"];

  return (
    <div className={`mt-4 p-4 rounded-2xl bg-[#FBF7F3] border border-[#E5276A]/20 ${inline ? "" : "fixed inset-0 z-50"}`} data-testid="upgrade-nudge-chat">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-[#E5276A] flex items-center justify-center text-white text-[10px] font-bold">A</div>
          <span className="text-xs font-bold text-[#1F3A5F]">{lang === "fr" ? "geoVINUM AI · mode conseil" : "geoVINUM AI · advisor mode"}</span>
        </div>
        <button onClick={onClose} className="text-[#8B95A1] hover:text-[#1F3A5F]"><X className="w-4 h-4" /></button>
      </div>

      <div className="max-h-72 overflow-y-auto space-y-3 pr-1" data-testid="upgrade-nudge-chat-history">
        {history.length === 0 && (
          <div className="text-xs text-[#4A5568] italic">
            {lang === "fr" ? "Posez n'importe quelle question — geoVINUM AI répond honnêtement, sans pression." : "Ask anything — geoVINUM AI answers honestly, no pressure."}
          </div>
        )}
        {history.map((msg, i) => (
          <div key={i} className={msg.role === "user" ? "text-right" : "text-left"} data-testid={`upgrade-nudge-msg-${i}`}>
            <div className={`inline-block max-w-[85%] p-3 rounded-xl text-sm ${
              msg.role === "user" ? "bg-[#1F3A5F] text-white" : "bg-white text-[#1F3A5F] border border-[#1F3A5F]/8"
            }`}>
              {msg.text}
              {msg.honest_note && (
                <div className="mt-2 text-xs italic text-amber-700 bg-amber-50 p-2 rounded-lg">⚠️ {msg.honest_note}</div>
              )}
              {msg.arguments?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {msg.arguments.map((a, j) => (
                    <li key={j} className="text-xs flex gap-1.5"><span className="text-[#E5276A]">→</span> {a}</li>
                  ))}
                </ul>
              )}
              {msg.action_label && msg.action !== "try_free" && (
                <button onClick={() => onAction(msg.action)} data-testid={`upgrade-nudge-action-${i}`}
                  className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#E5276A] text-white text-xs font-bold hover:bg-[#C01661]">
                  {msg.action_label} <ChevronRight className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="text-left">
            <div className="inline-block p-3 rounded-xl text-sm bg-white border border-[#1F3A5F]/8">
              <Loader2 className="w-4 h-4 animate-spin inline" /> <span className="text-xs text-[#8B95A1]">{lang === "fr" ? "geoVINUM AI réfléchit…" : "geoVINUM AI is thinking…"}</span>
            </div>
          </div>
        )}
      </div>

      {history.length === 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {SUGGESTIONS.map((s, i) => (
            <button key={i} onClick={() => ask(s)} data-testid={`upgrade-nudge-suggest-${i}`}
              className="text-[11px] px-2.5 py-1 rounded-full bg-white border border-[#1F3A5F]/12 text-[#1F3A5F] hover:border-[#E5276A] hover:text-[#E5276A]">
              {s}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="mt-3 flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === "fr" ? "Votre question…" : "Your question…"}
          data-testid="upgrade-nudge-input"
          className="flex-1 bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] outline-none rounded-lg px-3 py-2 text-sm text-[#1F3A5F]" />
        <button type="submit" disabled={busy || !q} data-testid="upgrade-nudge-send"
          className="px-3 py-2 rounded-lg bg-[#E5276A] text-white disabled:opacity-50">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
